import { stubTrue } from "lodash/fp";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import AccessRedirect from "@app/analytics/AccessRedirect";
import FreeTrialAlert from "@app/analytics/account/components/FreeTrialAlert";
import Tabs from "@app/analytics/components/Tabs";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import ROUTES from "@app/common/routes";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import LoadSpinner from "@rui/LoadSpinner";
import Stack from "@rui/Stack";

import People from "./People";
import Permissions from "./Permissions";
import Settings from "./Settings";
import Teams from "./Teams";

const WORKSPACE_TABS = [
  {
    id: "teams",
    label: "Teams",
    defaultPath: "teams",
    path: "teams/*",
    enabled: stubTrue,
  },
  {
    id: "people",
    label: "People",
    path: "people",
    enabled: (canEditWorkspaceSettings: boolean) => canEditWorkspaceSettings,
  },
  {
    id: "settings",
    label: "Settings",
    path: "settings",
    enabled: stubTrue,
  },
  {
    id: "permissions",
    label: "Permissions",
    path: "permissions",
    enabled: (canEditWorkspaceSettings: boolean) => canEditWorkspaceSettings,
  },
];

const WorkspaceSettings = () => {
  usePageTitle("Workspace Settings");

  const { workspaceId: workspaceIdParam } = useParams();
  const { workspaceId, workspaceLoading, inWorkspace } = useWorkspace();
  const { canEditWorkspaceSettings } = useFeatureAccess();

  // redirect users that are either not a part of a workspace or are
  // trying to view the wrong workspace
  if (!workspaceLoading) {
    if (!inWorkspace || workspaceIdParam === "undefined") {
      return <Navigate to={ROUTES.USER_SETTINGS_DEFAULT_SECTION} replace />;
    } else if (inWorkspace && workspaceId !== workspaceIdParam) {
      const route = workspaceId
        ? ROUTES.workspaceSettingsById(workspaceId)
        : ROUTES.USER_SETTINGS_DEFAULT_SECTION;
      return <Navigate to={route} replace />;
    }
  }

  const enabledTabs = WORKSPACE_TABS.filter((tab) =>
    tab.enabled(canEditWorkspaceSettings)
  );

  const defaultRoute = ROUTES.workspaceSettingsById(workspaceId!);

  return (
    <Stack
      alignItems="start"
      sx={{
        flexGrow: 1,
        bgcolor: "#fff",
        p: 2,
        pl: 3,
        pb: 0,
      }}
    >
      {workspaceLoading ? (
        <Stack variant="center" sx={{ height: "50%", width: "100%" }}>
          <LoadSpinner />
        </Stack>
      ) : (
        <>
          <FreeTrialAlert sx={{ mb: 2 }} />
          <Tabs tabs={enabledTabs} defaultRoute={defaultRoute} />
          <Stack
            sx={{
              height: "100%",
              width: "100%",
              maxHeight: "100%",
              overflowY: "auto",
              pt: 2,
            }}
          >
            <Stack sx={{ height: "100%" }}>
              <Routes>
                <Route path="teams/*" element={<Teams />} />
                <Route
                  path="people"
                  element={
                    <AccessRedirect
                      hasAccess={canEditWorkspaceSettings}
                      redirectTo={defaultRoute}
                    >
                      <People />
                    </AccessRedirect>
                  }
                />
                <Route path="settings" element={<Settings />} />
                <Route
                  path="permissions"
                  element={
                    <AccessRedirect
                      hasAccess={canEditWorkspaceSettings}
                      redirectTo={defaultRoute}
                    >
                      <Permissions />
                    </AccessRedirect>
                  }
                />
              </Routes>
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default WorkspaceSettings;
